import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import axios from 'axios';

const STRAPI_URL = import.meta.env.REACT_APP_STRAPI_URL;
const STRAPI_TOKEN = import.meta.env.REACT_APP_STRAPI_TOKEN;

type StrapiErrorResponse = {
  error?: {
    message?: string;
  };
};

export const strapiService = axios.create({
  baseURL: `${STRAPI_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

strapiService.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (STRAPI_TOKEN) {
    config.headers.Authorization = `Bearer ${STRAPI_TOKEN}`;
  }

  // Inject draft status globally for Staging environment
  if (import.meta.env.REACT_APP_MODE === 'next') {
    config.params = {
      status: 'draft', // Default to draft
      ...config.params, // Spread existing params so they take precedence if provided
    };
  }

  return config;
});

strapiService.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data.error) {
      throw new Error(response.data.error.message || 'Unknown Strapi Error');
    }

    return response.data;
  },
  async (error: AxiosError) => {
    const strapiError = (error.response?.data as StrapiErrorResponse | undefined)?.error;
    const errorMessage = strapiError?.message || error.message || 'Strapi Request Failed';

    console.error('[Strapi API Error]:', errorMessage);

    return Promise.reject(new Error(errorMessage));
  },
);

export async function strapiFetch<T>(
  endpoint: string,
  config: AxiosRequestConfig = {},
): Promise<T> {
  const response = await strapiService.request<T>({
    url: endpoint,
    ...config,
  });

  return response as unknown as T;
}
